import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import {
  Autocomplete,
  Button,
  Checkbox,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { Stack } from "@mui/system";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { StaticDateRangePicker } from "@mui/x-date-pickers-pro/StaticDateRangePicker";
import {
  colors,
  spacing,
  typography,
} from "../../../../../../../../assets/styles/tokens";
import { RangeCalendarHeader } from "../../../../../helpers/RangeCalendarHeader";
import {
  type CommittedSelection,
  type CompareTimeframeValue,
  getPrimaryLabel,
  PRIMARY_LIST_OPTIONS,
  useTimeFrameCompareState,
} from "../../../../../helpers/timeFrameCompareState";

// Compare-to only ever offers these two on mobile (Yesterday is never a
// comparison choice here, unlike desktop's full three-option set).
const MOBILE_COMPARE_CANDIDATES: CompareTimeframeValue[] = [
  "two_days_ago",
  "three_days_ago",
];

// Primary list items get a divider after these values, grouping the
// relative-day options / MTD / Custom range visually.
const DIVIDER_AFTER = new Set(["three_days_ago", "mtd"]);

const Puller = styled("div")(({ theme }) => ({
  width: spacing.Spacing["4xl"].$value,
  height: spacing.Spacing.xs.$value,
  backgroundColor: colors.GrayLightMode[300].$value,
  borderRadius: spacing.Radius.xs.$value,
  position: "absolute",
  top: spacing.Spacing.xl.$value,
  left: "calc(50% - 15px)",
  ...theme.applyStyles("dark", {
    backgroundColor: colors.GrayLightMode[300].$value,
  }),
}));

interface PerformanceDateTimeDrawerProps {
  value: CommittedSelection;
  onApply: (next: CommittedSelection) => void;
  hasData: boolean;
  disabled?: boolean;
}

export default function PerformanceDateTimeDrawer({
  value,
  onApply,
  hasData,
  disabled,
}: PerformanceDateTimeDrawerProps) {
  const {
    open,
    view,
    staged,
    isComparePending,
    isApplyEnabled,
    isCompareDisabled,
    compareOptions,
    handleOpen: handleOpenState,
    handleDiscard,
    handleApply,
    handleCancelCustom,
    handleSelectPrimary,
    handleCompareToggle,
    setCustomRange,
    setCompareValue,
  } = useTimeFrameCompareState({
    value,
    onApply,
    hasData,
    dismissOnCustomExit: false,
    compareCandidates: MOBILE_COMPARE_CANDIDATES,
    excludeCurrentPrimaryFromCompare: false,
  });

  const handleOpen = () => {
    if (disabled) return;
    handleOpenState();
  };

  const triggerLabel = getPrimaryLabel(
    value.primary,
    value.customStart,
    value.customEnd,
  );

  const drawer = (
    <Box role="dateTimeFilter">
      <Puller />
      <Stack justifyContent="end" direction="row" p={1}>
        <IconButton
          aria-label="Close"
          onClick={handleDiscard}
          sx={{
            width: spacing.Spacing["3xl"].$value,
            height: spacing.Spacing["3xl"].$value,
            color: colors.GrayLightMode[700].$value,
          }}
        >
          <CloseIcon />
        </IconButton>
      </Stack>

      <Typography
        variant="body1"
        sx={{
          fontSize: typography.Size.Text.sm.$value,
          color: colors.GrayLightMode[700].$value,
          mb: spacing.Spacing.md.$value,
        }}
      >
        Select Date Range
      </Typography>

      {view === "calendar" ? (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: spacing.Spacing.md.$value,
            }}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <StaticDateRangePicker
                calendars={1}
                value={[staged.customStart, staged.customEnd]}
                onChange={(newValue) =>
                  setCustomRange(newValue[0], newValue[1])
                }
                slots={{ calendarHeader: RangeCalendarHeader }}
                slotProps={{
                  actionBar: { actions: [] },
                  toolbar: { hidden: true },
                }}
              />
            </LocalizationProvider>
          </Box>

          <Stack direction="row" spacing={1}>
            <Button
              data-tracker="815"
              fullWidth
              variant="outlined"
              color="secondary"
              disabled={disabled}
              sx={{ textTransform: "capitalize", borderRadius: 2 }}
              onClick={handleCancelCustom}
            >
              Cancel
            </Button>
            <Button
              data-tracker="816"
              fullWidth
              variant="contained"
              color="secondary"
              disabled={!isApplyEnabled || disabled}
              sx={{
                textTransform: "capitalize",
                boxShadow: "none !important",
                background: colors.Brand[600].$value,
                borderRadius: 2,
              }}
              onClick={handleApply}
            >
              Apply
            </Button>
          </Stack>
        </>
      ) : (
        <>
          <List disablePadding sx={{ mb: spacing.Spacing.md.$value }}>
            {PRIMARY_LIST_OPTIONS.map((opt) => {
              const selected = staged.primary === opt.value;
              return (
                <Box key={opt.value}>
                  <ListItemButton
                    selected={selected}
                    disabled={disabled}
                    onClick={() => handleSelectPrimary(opt.value)}
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      "&.Mui-selected": {
                        backgroundColor: colors.Brand[50].$value,
                        color: colors.Brand[600].$value,
                        "&:hover": {
                          backgroundColor: colors.Brand[50].$value,
                        },
                      },
                    }}
                  >
                    {opt.value === "custom" && (
                      <CalendarMonthIcon
                        fontSize="small"
                        sx={{ mr: 1, color: "inherit" }}
                      />
                    )}
                    <ListItemText
                      primary={opt.listLabel}
                      slotProps={{
                        primary: { fontSize: typography.Size.Text.sm.$value },
                      }}
                    />
                  </ListItemButton>
                  {DIVIDER_AFTER.has(opt.value) && (
                    <Divider sx={{ my: 0.5 }} />
                  )}
                </Box>
              );
            })}
          </List>

          <Divider sx={{ mb: spacing.Spacing.md.$value }} />

          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ mb: spacing.Spacing.md.$value }}
          >
            <Checkbox
              checked={staged.compareEnabled}
              disabled={isCompareDisabled || disabled}
              size="small"
              onChange={(e) => handleCompareToggle(e.target.checked)}
              sx={{
                "&.Mui-checked": { color: colors.Brand[600].$value },
              }}
            />
            <Typography
              sx={{
                fontSize: typography.Size.Text.sm.$value,
                color: colors.GrayLightMode[700].$value,
                whiteSpace: "nowrap",
              }}
            >
              Compare to
            </Typography>
            <Autocomplete
              size="small"
              fullWidth
              disabled={isCompareDisabled || !staged.compareEnabled || disabled}
              options={compareOptions}
              value={
                compareOptions.find((o) => o.value === staged.compare) ?? null
              }
              isOptionEqualToValue={(o, v) => o.value === v.value}
              onChange={(_, newValue) =>
                setCompareValue(newValue?.value ?? null)
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  color="secondary"
                  placeholder="Comparison date"
                />
              )}
            />
          </Stack>

          {isComparePending && (
            <Button
              data-tracker="816"
              fullWidth
              variant="contained"
              color="secondary"
              disabled={!isApplyEnabled || disabled}
              sx={{
                textTransform: "capitalize",
                boxShadow: "none !important",
                background: colors.Brand[600].$value,
                borderRadius: 2,
              }}
              onClick={handleApply}
            >
              Apply
            </Button>
          )}
        </>
      )}
    </Box>
  );

  return (
    <>
      <Box
        data-tracker="805"
        onClick={handleOpen}
        component="span"
        display="flex"
        alignItems="center"
        gap={0.5}
        sx={{
          width: "228px",
          height: "32px",
          px: 1,
          border: `1px solid ${colors.GrayLightMode[300].$value}`,
          borderRadius: spacing.Radius.md.$value,
          cursor: disabled ? "default" : "pointer",
          opacity: disabled ? 0.5 : 1,
          pointerEvents: disabled ? "none" : "auto",
        }}
      >
        <Typography
          noWrap
          sx={{
            fontSize: typography.Size.Text.sm.$value,
            lineHeight: typography.Line_height.text.sm.$value,
            fontWeight: typography.Font_weight.Semibold.$value,
            color: colors.GrayLightMode[700].$value,
            flex: 1,
          }}
        >
          {triggerLabel}
        </Typography>
        <ArrowDropDownIcon
          fontSize="small"
          sx={{ color: colors.GrayLightMode[700].$value }}
        />
      </Box>

      <Drawer
        anchor="bottom"
        open={open}
        onClose={handleDiscard}
        PaperProps={{
          sx: {
            borderTopLeftRadius: spacing.Radius.xl.$value,
            borderTopRightRadius: spacing.Radius.xl.$value,
            p: spacing.Spacing.xl.$value + "!important",
          },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
}
